import { Component as BrComponent, Page } from '@bloomreach/spa-sdk';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ColDef,
  ColGroupDef,
  ValueGetterParams,
} from '@ag-grid-community/core';
import {
  AgGridService,
  InfoItem,
  ResponsiveService,
} from '@frk/eds-components';
import {
  ProductDetailConfiguration,
  TradingInformationData,
  TradingInformationState,
} from '@types';

import { AgGridAngular } from '@ag-grid-community/angular';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import { DropdownItem } from '@frk/eds-components';
import { Exchange } from '@models';
import { GridFilterGroupHeaderComponent } from './grid-filter-group-header/grid-filter-group-header.component';
import { Logger } from '@utils/logger';
import { Observable } from 'rxjs';
import { OverviewComponentConfig } from '@products/overview/overview-component.config';
import { OverviewDisplayComponent } from '@products/overview/overview-display-component.interface';
import { Subscription } from 'rxjs';
import { TradingInformationService } from '../services/trading-information.service';
import { TranslateService } from '@shared/translate/translate.service';

const logger = Logger.getLogger('TradingInformationComponent');

@Component({
  selector: 'ft-trading-information',
  templateUrl: './trading-information.component.html',
  styleUrls: ['./trading-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TradingInformationComponent
  extends BaseProductDetailComponent
  implements OverviewDisplayComponent, OnInit, OnChanges, OnDestroy {
  @Input() data: TradingInformationData;
  public isPopulated = false;
  componentConfiguration: OverviewComponentConfig;
  dataSubscription: Subscription;
  tradingInformationInfo: TradingInformationData;
  isHandheld$: Observable<boolean>;
  exchangeItems: DropdownItem[];

  // *** AG-Grid related declarations ***
  gridContext = {
    filteredRows: null,
  };
  frameworkComponents = {
    gridFilterGroupHeaderComponent: GridFilterGroupHeaderComponent,
  };
  defaultColDef: ColDef = {
    sortable: true,
  };

  columnDefs: ColGroupDef[] = [];
  sortedTableRows: Exchange[] = [];
  noRowsTemplate: string;

  @ViewChild('tradingGrid', { static: false, read: AgGridAngular })
  tradingGrid: AgGridAngular;
  @ViewChild('tradingGrid', { static: false, read: ElementRef })
  tradingEl: ElementRef;

  constructor(
    private tradingInformationService: TradingInformationService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private responsiveService: ResponsiveService,
    private agGridService: AgGridService
  ) {
    super();
  }

  ngOnInit(): void {
    this.isHandheld$ = this.responsiveService.isHandheld$();
  }

  public onGridReady(): void {
    this.agGridService.registerGrid(this.tradingGrid, this.tradingEl);
  }

  populate(
    componentConfig: OverviewComponentConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ) {
    this.initialize(brConfig, component, page);
    logger.debug(page, this.page);
    if (this.brConfig.fundId) {
      this.tradingInformationService.setConfigurationName(
        componentConfig.configurationName
      );
      this.dataSubscription = this.tradingInformationService.tradingInformationState.subscribe(
        (newState: TradingInformationState) => this.mapState(newState)
      );
      this.tradingInformationService.populate(brConfig);
    }
  }

  /**
   * Called when state changes.
   * @param tradingInformationState the new state returned by Trading information service
   */
  mapState(tradingInformationState: TradingInformationState) {
    this.columnDefs = this.getColumnDefsForTradingInfo();
    this.tradingInformationInfo = tradingInformationState.data;
    this.sortedTableRows = this.tradingInformationInfo.exchanges;
    this.exchangeItems = this.getDropdownData();
    this.cdr.detectChanges();
  }

  /**
   *  trading information coldef
   */
  getColumnDefsForTradingInfo() {
    return [
      {
        headerGroupComponent: 'gridFilterGroupHeaderComponent',
        headerGroupComponentParams: {
          parentComponent: this,
        },
        children: [
          {
            colId: 'exchangeName',
            headerName: this.translateService.instant(
              'pds.EXCHANGE_NAME',
              this.brConfig?.fundId
            ),
            valueGetter: (params) => {
              return (params.data as Exchange).exchangeName;
            },
            sortable: true,
            autoHeight: true,
            wrapText: true,
          },
          {
            colId: 'ticker',
            headerName: this.translateService.instant(
              'pds.SECURITY_TICKER',
              this.brConfig?.fundId
            ),
            valueGetter: (params) => {
              return (params.data as Exchange).ticker;
            },
            sortable: true,
            autoHeight: true,
          },
          {
            colId: 'tradeCurrency',
            headerName: this.translateService.instant(
              'pds.MARKET_CURRENCY',
              this.brConfig?.fundId
            ),
            valueGetter: (params) => {
              return (params.data as Exchange).tradeCurrency;
            },
            sortable: true,
            autoHeight: true,
          },
          {
            colId: 'bloomberg',
            headerName: this.translateService.instant(
              'pds.BLOOMBERG_IDENTIFIER',
              this.brConfig?.fundId
            ),
            valueGetter: (params) => {
              return (params.data as Exchange).bloomberg;
            },
            sortable: true,
            autoHeight: true,
          },
          {
            colId: 'reuters',
            headerName: this.translateService.instant(
              'pds.REUTERS_SYMBOL',
              this.brConfig?.fundId
            ),
            valueGetter: (params) => {
              return (params.data as Exchange).reuters;
            },
            sortable: true,
            autoHeight: true,
          },
          {
            colId: 'sedol',
            headerName: this.translateService.instant(
              'pds.SEDOL_NUMBER',
              this.brConfig?.fundId
            ),
            valueGetter: (params) => {
              return (params.data as Exchange).sedol;
            },
            sortable: true,
            autoHeight: true,
          },
        ],
      },
    ];
  }

  /**
   * Return info item object for a particular holding & column definition.
   * @param exchanges exchanges object
   * @param colDef column definition
   */
  getInfoItem(exchanges: Exchange, colDef: ColDef): InfoItem {
    let val = '';

    if (colDef.valueGetter) {
      if (typeof colDef.valueGetter === 'string') {
        val = colDef.valueGetter;
      } else {
        val = colDef.valueGetter({
          data: exchanges,
        } as ValueGetterParams);
      }
    }

    return {
      label: colDef.headerName,
      val,
    };
  }

  /**
   * Filter based on exchange name
   */
  getDropdownData(): DropdownItem[] {
    return this.filterdata();
  }

  private filterdata(): DropdownItem[] {
    // let exArr: string[] = [];
    // let exLabel: string[] = [];
    const exArr: string[] = this.tradingInformationInfo.exchanges.map(
      (data) => data.exchangeName
    );
    const uniqueArr = [...new Set(exArr)];
    uniqueArr.unshift('ALL_EXCHANGES');
    const exLabel: DropdownItem[] = uniqueArr.map((key) => {
      return {
        label:
          key === 'ALL_EXCHANGES'
            ? this.translateService.instant('products.list-all-exchanges')
            : key,
        value: key,
      };
    });
    return exLabel;
  }

  /**
   * Change detection on sorting
   */
  onSortChanged = () => this.cdr.detectChanges();

  /**
   * Filter exchanges table based upon dropdown selected.
   */
  filterExchange(selectedItem: DropdownItem): void {
    this.sortedTableRows = this.tradingInformationInfo.exchanges.filter(
      (exchange) => {
        if (selectedItem.value === 'ALL_EXCHANGES') {
          return this.tradingInformationInfo.exchanges;
        } else {
          return exchange.exchangeName === selectedItem.value;
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.agGridService.removeGrid(this.tradingGrid);
    // clean up subscription to avoid memory leaks
    this.dataSubscription?.unsubscribe();
  }
}
